// ✅ Trekkin.app — historyNative.js
// Utilitário para guardar/ler GPX do histórico no storage nativo (Capacitor)

import { Capacitor } from "@capacitor/core";
import { Directory, Encoding, Filesystem } from "@capacitor/filesystem";

export const isIOSNativeContainer = () => {
  if (typeof window === "undefined") return false;
  try {
    const platform =
      Capacitor?.getPlatform?.() || window?.Capacitor?.getPlatform?.() || "";
    const userAgent = window?.navigator?.userAgent || "";
    const hasDirectBridge =
      typeof window?.webkit?.messageHandlers?.nativeBridge?.postMessage ===
      "function";
    return platform === "ios" || (hasDirectBridge && /iPad|iPhone|iPod/i.test(userAgent));
  } catch {
    return false;
  }
};

export const isNativePlatform = () =>
  typeof window !== "undefined" &&
  (isIOSNativeContainer() ||
    (!!Capacitor?.isNativePlatform?.() && !!Filesystem?.writeFile) ||
    (!!window.Capacitor?.isNativePlatform?.() &&
      (!!window.Capacitor?.Plugins?.Filesystem?.writeFile || !!Filesystem?.writeFile)));

const safeName = (name) =>
  String(name || "track")
    .trim()
    .replace(/\.gpx$/i, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "") || "track";

export const buildHistoryGpx = (points, name, heartRate = null) => {
  if (!Array.isArray(points) || points.length < 1) return "";
  const safe = name || "Trekkin Track";
  const heartRateSamples = Array.isArray(heartRate?.samples)
    ? heartRate.samples
        .map((sample) => ({ bpm: Number(sample?.bpm), ts: Number(sample?.ts || sample?.time || 0) }))
        .filter((sample) => Number.isFinite(sample.bpm) && sample.bpm >= 25 && sample.bpm <= 260 && sample.ts > 0)
        .sort((a, b) => a.ts - b.ts)
    : [];
  // Keep the actual sensor timeline independently of GPS fixes. A sparse GPS
  // track (or a recording restored after a WebView restart) must not discard
  // BPM readings just because no position was recorded within 30 seconds.
  const heartRateExtension = heartRateSamples.length
    ? `<extensions><trekkin:heartRateSamples>${heartRateSamples
        .map((sample) => `<trekkin:hrSample ts="${new Date(sample.ts).toISOString()}">${Math.round(sample.bpm)}</trekkin:hrSample>`)
        .join("")}</trekkin:heartRateSamples></extensions>\n`
    : "";
  let heartRateIndex = 0;
  const heartRateForTime = (rawTs) => {
    const ts = Number(rawTs || 0);
    if (!ts || !heartRateSamples.length) return null;
    while (
      heartRateIndex + 1 < heartRateSamples.length &&
      Math.abs(heartRateSamples[heartRateIndex + 1].ts - ts) <=
        Math.abs(heartRateSamples[heartRateIndex].ts - ts)
    ) heartRateIndex += 1;
    const sample = heartRateSamples[heartRateIndex];
    return Math.abs(sample.ts - ts) <= 30000 ? Math.round(sample.bpm) : null;
  };
  let lastEle = null;
  return (
    `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<gpx version="1.1" creator="Trekkin.app" xmlns="http://www.topografix.com/GPX/1/1" xmlns:gpxtpx="http://www.garmin.com/xmlschemas/TrackPointExtension/v1" xmlns:trekkin="https://trekkin.pt/gpx/1">\n<trk>\n` +
    `<name>${safe}</name>\n${heartRateExtension}<trkseg>\n` +
    points
      .map((p) => {
        const rawEle =
          typeof p.ele === "number"
            ? p.ele
            : typeof p.alt === "number"
            ? p.alt
            : null;
        if (Number.isFinite(rawEle)) lastEle = rawEle;
        const eleVal = Number.isFinite(rawEle)
          ? rawEle
          : Number.isFinite(lastEle)
          ? lastEle
          : 0;
        const ele = `<ele>${Number(eleVal).toFixed(1)}</ele>`;
        const ts = p.ts || p.time;
        const time = ts ? `<time>${new Date(ts).toISOString()}</time>` : "";
        const bpm = heartRateForTime(ts);
        const extensions = bpm == null
          ? ""
          : `<extensions><gpxtpx:TrackPointExtension><gpxtpx:hr>${bpm}</gpxtpx:hr></gpxtpx:TrackPointExtension></extensions>`;
        return `<trkpt lat="${p.lat}" lon="${p.lon}">${ele}${time}${extensions}</trkpt>`;
      })
      .join("\n") +
    `\n</trkseg>\n</trk>\n</gpx>`
  );
};

export const writeHistoryGpxNative = async (gpxText, name) => {
  const fs = Filesystem || window?.Capacitor?.Plugins?.Filesystem;
  if (!fs?.writeFile) return null;
  const filename = `${safeName(name)}-${Date.now()}.gpx`;
  const path = `history_${filename}`;
  const directory = Directory?.Data || "DATA";
  const encoding = Encoding?.UTF8 || "utf8";
  try {
    await fs.writeFile({
      path,
      data: gpxText,
      directory,
      encoding,
    });
    return { path, directory, filename };
  } catch {
    try {
      const b64 = btoa(unescape(encodeURIComponent(gpxText)));
      await fs.writeFile({
        path,
        data: b64,
        directory,
      });
      return { path, directory, filename, base64: true };
    } catch {
      return null;
    }
  }
};

const resolveNativeHistoryRef = (entry) => {
  const candidates = [
    entry?.native?.gpx,
    entry?.nativeRef,
    entry?.gpxFile,
    entry?.nativeGpx,
  ];

  for (const ref of candidates) {
    if (!ref) continue;
    if (typeof ref === "string") {
      const path = ref.trim();
      if (path) return { path, directory: "DATA" };
      continue;
    }
    if (typeof ref === "object" && String(ref.path || "").trim()) {
      return {
        ...ref,
        path: String(ref.path).trim(),
        directory: ref.directory || "DATA",
      };
    }
  }
  return null;
};

export const readHistoryGpxNative = async (entry) => {
  const fs = Filesystem || window?.Capacitor?.Plugins?.Filesystem;
  if (!fs?.readFile) return null;
  const ref = resolveNativeHistoryRef(entry);
  if (!ref?.path) return null;
  const directory = ref.directory || Directory?.Data || "DATA";
  const encoding = Encoding?.UTF8 || "utf8";
  try {
    const res = await fs.readFile({ path: ref.path, directory, encoding });
    if (typeof res?.data === "string") return res.data;
  } catch {}
  try {
    const res = await fs.readFile({ path: ref.path, directory });
    let data = res?.data;
    if (typeof data === "string") {
      try {
        data = decodeURIComponent(escape(atob(data)));
      } catch {}
      return data;
    }
  } catch {}
  return null;
};
